import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, LogIn, Loader2, ShieldCheck, AlertCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import PasswordStrengthChecklist, { passwordIsStrong } from "@/components/auth/PasswordStrengthChecklist";

/**
 * AuthGate — inline sign-in / quick-register modal that the chauffeur flow
 * pops up the moment a guest tries to post a ride. Saves the half-filled
 * form by NOT unmounting the parent, then `onSuccess()` lets the parent
 * call its submit function again with credentials in place.
 *
 * Two tabs: Sign In | Sign up with email. Quick-signup asks only for
 * email + password — name auto-derives from the email's local-part if
 * the user doesn't supply one.
 */
export default function AuthGate({ open, onClose, onSuccess, reason, defaultTab = "login" }) {
  const [tab, setTab] = useState(defaultTab);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "",
  });
  // Re-sync the active tab when the parent changes its requested defaultTab
  // (e.g. user clicks "Sign in" → "Sign up with email" → reopens modal).
  useEffect(() => {
    if (open) setTab(defaultTab);
  }, [open, defaultTab]);
  const set = (patch) => setForm((s) => ({ ...s, ...patch }));

  // Mirrors the backend rules so registration cannot fail on rules.
  const allRulesPass = useMemo(() => passwordIsStrong(form.password), [form.password]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.email || !form.password) { toast.error("Email and password are required"); return; }
    if (tab === "register" && !allRulesPass) {
      toast.error("Password must meet all the rules below");
      return;
    }
    setSubmitting(true);
    try {
      if (tab === "register") {
        // Auto-derive name from email local-part so the user doesn't have to
        // type one. They can update it later from the profile page.
        const fallbackName = form.email.split("@")[0].replace(/[._-]+/g, " ").trim() || "Member";
        await api.post("/auth/register", {
          name: form.name || fallbackName,
          email: form.email,
          phone: form.phone || null,
          password: form.password,
        });
      }
      const lr = await api.post("/auth/login", {
        email: form.email, password: form.password,
      });
      const tok = lr.data?.access_token || lr.data?.token;
      if (!tok) throw new Error("No token returned");
      localStorage.setItem("auth_token", tok);
      toast.success(tab === "register" ? "Account created — you're signed in." : "Signed in.");
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(errorMessage(err, "Could not sign in"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="auth-gate-modal"
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
            className="bg-zinc-950 border border-gold/30 rounded-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gold/15 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-gold" />
                <div className="text-gold font-display text-lg">Sign in to continue</div>
              </div>
              <button data-testid="auth-gate-close" onClick={onClose} className="text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {reason && (
              <div className="mx-6 mt-4 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-100/85">{reason}</div>
              </div>
            )}

            <div className="px-6 pt-4 flex gap-2">
              <button
                data-testid="auth-tab-login"
                onClick={() => setTab("login")}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  tab === "login" ? "bg-gold/15 text-gold border border-gold/40" : "text-white/60 border border-white/10 hover:border-gold/30"
                }`}
              >
                <LogIn className="w-4 h-4" /> Sign In
              </button>
              <button
                data-testid="auth-tab-register"
                onClick={() => setTab("register")}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  tab === "register" ? "bg-gold/15 text-gold border border-gold/40" : "text-white/60 border border-white/10 hover:border-gold/30"
                }`}
              >
                <Mail className="w-4 h-4" /> Sign up with email
              </button>
            </div>

            <form onSubmit={submit} className="p-6 space-y-3">
              {tab === "register" && (
                <p className="text-xs text-white/55">
                  Just two fields needed — your email and a password. Add your name and phone any time later.
                </p>
              )}
              <input
                data-testid="auth-input-email"
                type="email" value={form.email} onChange={(e) => set({ email: e.target.value })}
                placeholder="your@email.com"
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
                autoFocus
              />
              <input
                data-testid="auth-input-password"
                type="password" value={form.password} onChange={(e) => set({ password: e.target.value })}
                placeholder={tab === "register" ? "Create a password (12+ chars, mixed case, number, special)" : "Password"}
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
              />
              {tab === "register" && (
                <PasswordStrengthChecklist password={form.password} data-testid="auth-password-rules" />
              )}
              {tab === "register" && (
                <details className="text-xs text-white/55">
                  <summary className="cursor-pointer hover:text-gold transition-colors select-none">
                    Add name & phone (optional)
                  </summary>
                  <div className="mt-2 space-y-2">
                    <input
                      data-testid="auth-input-name"
                      type="text" value={form.name} onChange={(e) => set({ name: e.target.value })}
                      placeholder="Full name (auto-filled from email if blank)"
                      className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
                    />
                    <input
                      data-testid="auth-input-phone"
                      type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })}
                      placeholder="Phone (optional, for ride updates)"
                      className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-gold focus:outline-none"
                    />
                  </div>
                </details>
              )}
              <button
                data-testid="auth-submit"
                type="submit" disabled={submitting}
                className="w-full bg-gold text-black hover:bg-gold/90 rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {tab === "register" ? <><Mail className="w-4 h-4" /> Sign up with email</> : "Sign in & continue"}
              </button>
              {tab === "login" && (
                <p className="text-[11px] text-white/45 text-center pt-1">
                  No account?{" "}
                  <button type="button" onClick={() => setTab("register")} className="text-gold/80 hover:text-gold underline">
                    Sign up with email
                  </button>
                </p>
              )}
              <p className="text-[11px] text-white/40 text-center pt-1">
                Your pickup, drop-off, and notes will be preserved.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
